import forge from 'node-forge'
import { api } from './client'
import type { ApiEnvelope, Mail, PaginatedResponse } from '@/types/api'

interface SpringPage<T> {
    content: T[]
    totalElements: number
    totalPages: number
    number: number
    size: number
}

export interface MailQueryParams {
    mail?: string
    name?: string
    port?: string
    unitId?: string
    protocol?: number
    status?: number
    fromDate?: string
    toDate?: string
}

export interface MailWriteInput {
    serverUrl: string
    serverPort: number
    protocol: number
    mail: string
    name: string
    username: string
    password?: string
    unitId?: string
    status?: number
}

export interface TestConnectionInput {
    serverUrl: string
    serverPort: number
    protocol: number
    username: string
    password?: string
}

const MAIL_BASE = '/v1/robot/mail-server-management' 

function adaptPage<T>(
    raw: SpringPage<T> | PaginatedResponse<T> | T[] | null | undefined,
): PaginatedResponse<T> {
    if (!raw) return { items: [], total: 0, page: 1, limit: 10, totalPages: 0 }
    if (Array.isArray(raw)) return { items: raw, total: raw.length, page: 1, limit: Math.max(10, raw.length), totalPages: raw.length ? 1 : 0 }
    if ('items' in raw) return raw
    return {
        items: raw.content,
        total: raw.totalElements || 0,
        page: (raw.number || 0) + 1,
        limit: raw.size || 10,
        totalPages: raw.totalPages || 0,
    }
}

function toQueryParams(params: MailQueryParams = {}) {
    return {
        mail: params.mail?.trim() || undefined,
        name: params.name?.trim() || undefined,
        port: params.port?.trim() || undefined,
        unitId: params.unitId || undefined,
        protocol: params.protocol || undefined,
        status: params.status,
        fromDate: params.fromDate,
        toDate: params.toDate,
    }
}

function getErrorMessage(error: any, fallback: string) {
    return error?.response?.data?.responseMessage || error?.message || fallback
}

export async function getMailPublicKey(signal?: AbortSignal): Promise<string> {
    try {
        const response = await api.get<ApiEnvelope<string>>(`${MAIL_BASE}/public-key`, { signal }) //
        return response.data.responseData || (response.data as unknown as string)
    } catch (error: any) {
        throw new Error(getErrorMessage(error, 'Đã xảy ra lỗi khi lấy Public Key.'))
    }
}

export async function encryptMailPassword(password: string): Promise<string> {
    if (!password) return password;

    try {
        const rawPublicKey = await getMailPublicKey();
        if (!rawPublicKey) {
            throw new Error('Không lấy được Public Key.');
        }
        let pemKey = rawPublicKey.trim();
        if (!pemKey.includes('-----BEGIN PUBLIC KEY-----')) {
            const formattedBody = pemKey.replace(/\s+/g, '').match(/.{1,64}/g)?.join('\n') || pemKey;
            pemKey = `-----BEGIN PUBLIC KEY-----\n${formattedBody}\n-----END PUBLIC KEY-----`;
        }

        const publicKey = forge.pki.publicKeyFromPem(pemKey);
        const passwordBytes = forge.util.encodeUtf8(password);

        const encryptedBytes = publicKey.encrypt(passwordBytes);
        return forge.util.encode64(encryptedBytes);
    } catch (error) {
        throw new Error('Mã hóa mật khẩu thất bại. Vui lòng thử lại.');
    }
}

export async function fetchMails(
    page = 0,
    pageSize = 10,
    params: MailQueryParams = {},
    signal?: AbortSignal,
): Promise<PaginatedResponse<Mail>> {
    try {
        const response = await api.get<ApiEnvelope<SpringPage<Mail> | Mail[]>>(
            MAIL_BASE,
            {
                params: { page, size: pageSize, ...toQueryParams(params) }, //
                signal,
            },
        )
        return adaptPage(response.data.responseData)
    } catch (error: any) {
        throw new Error(getErrorMessage(error, 'Đã xảy ra lỗi khi lấy danh sách mail.'))
    }
}

export async function getMailById(id: string, signal?: AbortSignal): Promise<Mail> {
    try {
        const response = await api.get<ApiEnvelope<Mail>>(`${MAIL_BASE}/${id}`, { signal }) //
        return response.data.responseData
    } catch (error: any) {
        throw new Error(getErrorMessage(error, 'Đã xảy ra lỗi khi lấy chi tiết mail.'))
    }
}

export async function createMail(data: MailWriteInput): Promise<ApiEnvelope<Mail>> {
    try {
        const response = await api.post<ApiEnvelope<Mail>>(MAIL_BASE, data) //
        return response.data
    } catch (error: any) {
        throw new Error(getErrorMessage(error, 'Đã xảy ra lỗi khi tạo mail server.'))
    }
}

export async function updateMail(id: string, data: MailWriteInput): Promise<ApiEnvelope<Mail>> {
    try {
        const response = await api.put<ApiEnvelope<Mail>>(`${MAIL_BASE}/${id}`, data) //
        return response.data
    } catch (error: any) {
        throw new Error(getErrorMessage(error, 'Đã xảy ra lỗi khi cập nhật mail server.'))
    }
}

export async function updateMailStatus(mail: Mail, newStatus: number): Promise<ApiEnvelope<Mail>> {
    const fullMail = await getMailById(mail.id)
    return updateMail(mail.id, {
        serverUrl: fullMail.serverUrl,
        serverPort: fullMail.serverPort,
        protocol: fullMail.protocol,
        mail: fullMail.mail,
        name: fullMail.name,
        username: fullMail.username,
        password: fullMail.password,
        unitId: fullMail.unitId,
        status: newStatus,
    })
}

export async function deleteMail(id: string): Promise<ApiEnvelope<unknown>> {
    try {
        const response = await api.delete<ApiEnvelope<unknown>>(`${MAIL_BASE}/${id}`) //
        return response.data
    } catch (error: any) {
        throw new Error(getErrorMessage(error, 'Đã xảy ra lỗi khi xóa mail server.'))
    }
}

export async function testMailConnection(data: TestConnectionInput): Promise<ApiEnvelope<unknown>> {
    try {
        const response = await api.post<ApiEnvelope<unknown>>(`${MAIL_BASE}/test-connection`, data) //
        return response.data
    } catch (error: any) {
        throw new Error(getErrorMessage(error, 'Lỗi do hệ thống trả về.'))
    }
}